import { useCallback, useMemo, useState } from "react";

import { useEmployeeProfilesTrackingService } from "app/pages/sales-intelligence/modules/employee-profiles/hooks/useEmployeeProfilesTrackingService";
import type { EmployeeProfilesSegment } from "app/pages/sales-intelligence/modules/employee-profiles/types/employeeProfiles.types";
import {
    countEmployeeProfilesBySegment,
    splitEmployeeProfiles,
} from "app/pages/sales-intelligence/modules/employee-profiles/utils/splitEmployeeProfiles";
import { useEmployeeProfilesLibraryQuery } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useEmployeeProfilesLibraryQuery";

export const useEmployeeProfilesLibrary = () => {
    const employeeProfilesTracking = useEmployeeProfilesTrackingService();

    const [segment, setSegment] = useState<EmployeeProfilesSegment>("all");
    const [searchTerm, setSearchTerm] = useState("");

    const { data, isLoading, isError, refetch } = useEmployeeProfilesLibraryQuery();

    const profiles = useMemo(() => data?.profiles ?? [], [data]);

    const { sharedCount, privateCount } = useMemo(() => countEmployeeProfilesBySegment(profiles), [profiles]);

    const { sharedRows, privateRows } = useMemo(
        () => splitEmployeeProfiles(profiles, searchTerm),
        [profiles, searchTerm],
    );

    const showShared = (segment === "all" || segment === "shared") && sharedRows.length > 0;
    const showPrivate = (segment === "all" || segment === "private") && privateRows.length > 0;

    const handleSegmentChange = useCallback(
        (next: EmployeeProfilesSegment) => {
            employeeProfilesTracking.trackSegmentChange(next);
            setSegment(next);
        },
        [employeeProfilesTracking],
    );

    const handleRetry = useCallback(() => {
        employeeProfilesTracking.trackListRetry();
        void refetch();
    }, [employeeProfilesTracking, refetch]);

    const isSettled = !isLoading && !isError;

    return {
        segment,
        searchTerm,
        sharedRows,
        privateRows,
        sharedCount,
        privateCount,
        showShared,
        showPrivate,
        isLoading,
        isError,
        isLibraryEmpty: isSettled && profiles.length === 0,
        hasNoMatches: isSettled && profiles.length > 0 && !showShared && !showPrivate,
        showTables: isSettled && (showShared || showPrivate),
        onSearchChange: setSearchTerm,
        handleSegmentChange,
        handleRetry,
    };
};
